import Constants from 'expo-constants';
import {
  action, makeObservable, observable, runInAction,
} from 'mobx';
import { create, persist } from 'mobx-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';
import _ from 'lodash';

import type { RootStore } from './RootStore';
import {
  UserAuthRes, ErrorRes, ManifestExtra, MfaAuthRequiredRes,
} from '../types';
import I18n from '../i18n';

const { API_URL } = Constants.manifest?.extra as ManifestExtra;

const HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const hydrate = create({ storage: AsyncStorage, jsonify: true });

type AuthRes = UserAuthRes | MfaAuthRequiredRes;
type AuthBody = {
  email: string;
  pass: string;
};
type MfaAuthBody = {
  code: string;
  mfaChallengeToken: string;
};

const AUTH_TOKEN_COOKIE_PATH = '/auth/token/cookie';
const AUTH_TOKEN_COOKIE_MFA_PATH = '/auth/token/cookie/mfa';

const isObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

const isMfaRequiredResponse = (json: unknown): json is MfaAuthRequiredRes => {
  if (!isObject(json)) return false;
  return typeof json.mfaChallengeToken === 'string';
};

export default class AuthStore {
  store: RootStore;

  constructor(rootStore: RootStore) {
    this.store = rootStore;
    makeObservable(this);
    hydrate('auth', this).then(() => {
      this.hydrated = true;
    }).catch((err) => {
      // Call Sentry
      // eslint-disable-next-line no-console
      console.log(err);
    });
  }

  @observable hydrated = false;

  @persist @observable userId?: string;

  @persist @observable orgId?: string;

  @persist('list') @observable roles?: string[];

  @persist @observable xsrfToken = '';

  @observable mfaTotpEnabled?: boolean;

  @observable pendingMfaChallengeToken?: string;

  get pendingMfaLogin() {
    return !!this.pendingMfaChallengeToken;
  }

  applyAuthResponse = (json: AuthRes) => {
    if (json && 'userId' in json) {
      runInAction(() => {
        this.userId = json.userId;
        this.roles = json.roles;
        this.xsrfToken = json.xsrfToken;
        this.clearPendingMfaLogin();
      });
      return true;
    }
    return false;
  };

  @action setPendingMfaLogin = (mfaChallengeToken: string) => {
    this.pendingMfaChallengeToken = mfaChallengeToken;
  };

  @action clearPendingMfaLogin = () => {
    this.pendingMfaChallengeToken = undefined;
  };

  @action setMfaTotpEnabled = (enabled: boolean) => {
    this.mfaTotpEnabled = enabled;
  };

  requestAuthToken = async (path: string, body: AuthBody | MfaAuthBody): Promise<AuthRes> => {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: HEADERS,
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (res.status >= 400) {
      const jsonErr = await res.json() as ErrorRes & MfaAuthRequiredRes;
      const errMsg = jsonErr.code ? I18n.t(`error.api.${jsonErr.code}`) : I18n.t('error.unknownError');
      throw new Error(errMsg);
    }

    return res.json() as Promise<AuthRes>;
  };

  /** Auth for Web */
  @action logIn = async (email: string, pass: string) => {
    const json = await this.requestAuthToken(AUTH_TOKEN_COOKIE_PATH, { email, pass });

    if (isMfaRequiredResponse(json)) {
      this.setPendingMfaLogin(json.mfaChallengeToken);
      return;
    }

    if (!this.applyAuthResponse(json)) throw new Error(I18n.t('error.unknownError'));
  };

  @action verifyMfaLogin = async (code: string) => {
    if (!this.pendingMfaChallengeToken) {
      throw new Error(I18n.t('error.login.mfaLoginExpired'));
    }

    const json = await this.requestAuthToken(AUTH_TOKEN_COOKIE_MFA_PATH, {
      code,
      mfaChallengeToken: this.pendingMfaChallengeToken,
    });

    if (!this.applyAuthResponse(json)) throw new Error(I18n.t('error.unknownError'));
  };

  @action logOut = async () => {
    if (this.userId) {
      await fetch(`${API_URL}/auth/token/cookie`, {
        method: 'DELETE',
        headers: HEADERS,
        credentials: 'include',
      });
    }
    this.terminateSession();
  };

  @action terminateSession = () => {
    runInAction(() => {
      this.userId = undefined;
      this.orgId = undefined;
      this.roles = undefined;
      this.xsrfToken = '';
      this.mfaTotpEnabled = undefined;
      this.clearPendingMfaLogin();
      this.store.uiState.clearUiState();
      window.location.replace('/');
    });
  };

  @action checkAccess = (authorizedRoles: string[]) => {
    if (this.roles?.includes('admin')) return true;
    return _.intersection(this.roles, authorizedRoles).length > 0;
  };
}
